import {
  AbstractFieldModel,
  DateFieldModel,
  FileFieldModel,
  NumberFieldModel,
  RadioFieldModel,
  SelectFieldModel,
  TextAreaFieldModel,
  TextFieldModel,
  OptionModel,
  ValidateField,
} from '@cak-legacy/generation-schema-core';
import { AutoCompleteEnum } from '@cak-legacy/shared-common';

import { PredefinedBijlageField } from '../interface/SimpleForm/PredefinedBijlageField';
import { PredefinedField } from '../interface/SimpleForm/PredefinedField';
import { PredefinedRegelingField } from '../interface/SimpleForm/PredefinedRegelingField';
import { translate } from '../../locales';

export class FieldFactory {
  createEmailField(predefinedField: PredefinedField): TextFieldModel {
    const validate: ValidateField = {
      maxLength: {
        value: 50,
      },
      emailValidation: {
        value: true,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);
    return {
      ...this.getBaseModel(predefinedField),
      type: 'text',
      autoComplete: predefinedField.autoComplete ? AutoCompleteEnum.emailAdres : undefined,
      validate: validate,
    };
  }

  createBsnField(predefinedField: PredefinedField): TextFieldModel {
    const validate: ValidateField = {
      maxLength: {
        value: 50,
      },
      bsnValidation: {
        value: true,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);

    return {
      ...this.getBaseModel(predefinedField),
      type: 'text',
      validate: validate,
    };
  }

  createVoornaamField(predefinedField: PredefinedField): TextFieldModel {
    const validate: ValidateField = {
      maxLength: {
        value: 155,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);

    return {
      ...this.getBaseModel(predefinedField),
      type: 'text',
      validate: validate,
      autoComplete: predefinedField.autoComplete ? AutoCompleteEnum.voornaam : undefined,
    };
  }

  createVoorlettersField(predefinedField: PredefinedField): TextFieldModel {
    const validate: ValidateField = {
      maxLength: {
        value: 50,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);

    return {
      ...this.getBaseModel(predefinedField),
      type: 'text',
      validate: validate,
    };
  }

  createAchternaamField(predefinedField: PredefinedField): TextFieldModel {
    const validate: ValidateField = {
      maxLength: {
        value: 50,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);

    return {
      ...this.getBaseModel(predefinedField),
      type: 'text',
      autoComplete: predefinedField.autoComplete ? AutoCompleteEnum.achternaam : undefined,
      validate: validate,
    };
  }

  createGeslachtField(predefinedField: PredefinedField): RadioFieldModel {
    const validate: ValidateField = {};
    this.addRequired(validate, predefinedField.isRequired);

    return {
      options: [
        {
          value: 'man',
          label: translate('Man'),
        },
        {
          value: 'vrouw',
          label: translate('Vrouw'),
        },
        {
          value: 'Onbekend',
          label: translate('Onbekend'),
        },
      ],
      type: 'radio',
      validate: validate,
      ...this.getBaseModel(predefinedField),
    };
  }

  createBedragField(predefinedField: PredefinedField): NumberFieldModel {
    const validate: ValidateField = {
      maxLength: {
        value: 50,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);

    return {
      ...this.getBaseModel(predefinedField),
      type: 'number',
      decimalsEnabled: true,
      validate: validate,
      icon: 'icon icon__currency--euro',
      overview: {
        beforeText: {
          text: '€',
        },
      },
    };
  }

  createDatumField(predefinedField: PredefinedField): DateFieldModel {
    const validate: ValidateField = {};
    this.addRequired(validate, predefinedField.isRequired);

    return {
      ...this.getBaseModel(predefinedField),
      type: 'date',
      validate: validate,
    };
  }

  createGeboorteDatumField(predefinedField: PredefinedField): DateFieldModel {
    const validate: ValidateField = {
      minimumDate: {
        value: '01-01-1900',
      },
      noFutureDateValidation: {
        value: true,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);

    return {
      ...this.getBaseModel(predefinedField),
      type: 'date',
      validate: validate,
    };
  }

  createGeboorteNaamField(predefinedField: PredefinedField): TextFieldModel {
    return this.createTextField(predefinedField, 155);
  }

  createGeboortePlaatsField(predefinedField: PredefinedField): TextFieldModel {
    return this.createTextField(predefinedField, 200);
  }

  createAddressLineField(predefinedField: PredefinedField): TextFieldModel {
    return this.createTextField(predefinedField, 155);
  }

  createStraatField(predefinedField: PredefinedField): TextFieldModel {
    return this.createTextField(predefinedField, 155);
  }

  createHuisnummerField(predefinedField: PredefinedField): TextFieldModel {
    return this.createTextField(predefinedField, 155);
  }

  createPostcodeField(predefinedField: PredefinedField): TextFieldModel {
    return this.createTextField(predefinedField, 155);
  }

  createPlaatsField(predefinedField: PredefinedField): TextFieldModel {
    return this.createTextField(predefinedField, 155);
  }

  createProvincieField(predefinedField: PredefinedField): TextFieldModel {
    return this.createTextField(predefinedField, 155);
  }

  createLandField(predefinedField: PredefinedField): TextFieldModel {
    return this.createTextField(predefinedField, 155);
  }

  createRegelingField(predefinedField: PredefinedRegelingField): SelectFieldModel {
    const validate: ValidateField = {};
    this.addRequired(validate, predefinedField.isRequired);

    let options: OptionModel[] = [];
    predefinedField.regelingOptions.forEach((optie) => {
      options = [...options, { value: optie, label: translate(`Klantondersteuning_Formulieren_Regeling${optie}`) }];
    });

    return {
      ...this.getBaseModel(predefinedField),
      options: options,
      type: 'select',
      validate: validate,
      orderOptionsBy: predefinedField.orderOptionsBy,
    };
  }

  createBijlageField(predefinedField: PredefinedBijlageField): FileFieldModel {
    const maxFiles = predefinedField.maxFileAmount ?? 4;
    const labels =
      maxFiles > 1
        ? {
            infoText: 'Klantondersteuning_Formulieren_BijlagenInfoText',
            buttonText: 'Klantondersteuning_Formulieren_VoegBestandenToe',
          }
        : {
            infoText: 'Klantondersteuning_Formulieren_BijlageInfoText',
            buttonText: 'Klantondersteuning_Formulieren_VoegBestandToe',
          };
    const validate: ValidateField = {
      fileFieldValidation: {
        maxFileSizeInMb: 5,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);

    const extensions = predefinedField.allowedExentions ?? [
      'doc',
      'docx',
      'pdf',
      'txt',
      'ppt',
      'pptx',
      'png',
      'xls',
      'xlsx',
      'rtf',
      'gif',
      'jpg',
      'jpeg',
    ];

    return {
      ...this.getBaseModel(predefinedField),
      type: 'file',
      validate: validate,
      maxFiles: maxFiles,
      showValidationMessageBelowFile: true,
      buttonText: translate(labels.buttonText),
      infoText: translate(labels.infoText, {
        maxAmount: maxFiles.toString(),
      }),
      multiple: maxFiles > 1,
      showFileIndicatorLabel: true,
      fileIndicatorLabel: translate('Klantondersteuning_Formulieren_BijlageIndicatorLabel'),
      extensions: extensions,
      ui: {
        ...this.getBaseModel(predefinedField).ui,
        readOverview: {
          isFilesOverview: true,
        },
      },
    };
  }

  createTelefoonnummerField(predefinedField: PredefinedField): TextFieldModel {
    const validate: ValidateField = {
      maxLength: {
        value: 50,
      },
      phoneValidation: {
        value: true,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);

    return {
      ...this.getBaseModel(predefinedField),
      type: 'text',
      autoComplete: predefinedField.autoComplete ? AutoCompleteEnum.telefoonNummer : undefined,
      validate: validate,
    };
  }

  createKenmerkCakField(predefinedField: PredefinedField): TextFieldModel {
    return this.createTextField(predefinedField, 50);
  }

  createIbanField(predefinedField: PredefinedField): TextFieldModel {
    const validate: ValidateField = {
      maxLength: {
        value: 34,
      },
      ibanValidation: {
        value: true,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);

    return {
      ...this.getBaseModel(predefinedField),
      type: 'text',
      validate: validate,
    };
  }

  createTextField(predefinedField: PredefinedField, maxLength = 255): TextFieldModel {
    const validate: ValidateField = {
      maxLength: {
        value: maxLength,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);

    return {
      ...this.getBaseModel(predefinedField),
      type: 'text',
      validate: validate,
    };
  }

  createRadioYesNoField(predefinedField: PredefinedField): RadioFieldModel {
    const validate: ValidateField = {};
    this.addRequired(validate, predefinedField.isRequired);

    return {
      options: [
        {
          value: 'ja',
          label: translate('Ja'),
        },
        {
          value: 'nee',
          label: translate('Nee'),
        },
      ],
      type: 'radio',
      validate: validate,
      ...this.getBaseModel(predefinedField),
    };
  }

  createTextAreaField(predefinedField: PredefinedField, maxSize = 2000): TextAreaFieldModel {
    const validate: ValidateField = {
      maxLength: {
        value: maxSize,
      },
      limitMaxLengthOnInputs: {
        value: true,
      },
    };
    this.addRequired(validate, predefinedField.isRequired);

    return {
      ...this.getBaseModel(predefinedField),
      type: 'textarea',
      validate: validate,
      infoText: translate('Klantondersteuning_Formulieren_TextAreaInfoText{aantal}', {
        aantal: `${maxSize}`,
      }),
    };
  }

  private getBaseModel(predefinedField: PredefinedField): AbstractFieldModel {
    return {
      dependsOn: predefinedField.dependsOn,
      nestedDependsOn: predefinedField.nestedDependsOn,
      disabled: predefinedField.disabled,
      ui: {
        flexDirection: 'column',
        formGroupfullWidth: true,
      },
      showIsValidCheckmark: true,
    };
  }

  private addRequired(validate: ValidateField, isRequired: boolean | undefined) {
    if (isRequired) {
      validate.isRequired = {
        value: true,
      };
    }
  }
}
